import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  image: string;
  kicker: string;
  title: string;
  desc: string;
}

export default function HeroCarousel({ slides }: { slides: Slide[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const handleStep = (dir: number) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[460px] w-full md:h-[560px]">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={`出轨同学会剧照 · ${slide.title}`}
              title={`出轨同学会剧照 ${slide.title}`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/25"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent"></div>
          </div>
        ))}

        <div className="relative mx-auto flex h-full w-full max-w-[1440px] flex-col justify-center px-4 md:px-8 lg:px-12">
          <div key={index} className="animate-hero-in max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 font-label text-xs font-medium text-primary-950">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-fire-line text-xs leading-none"></i>
              </span>
              {slides[index].kicker}
            </span>
            <h1 className="mt-5 font-heading text-[26px] font-bold leading-tight text-background-50 md:text-4xl lg:text-[46px]">
              {slides[index].title}
            </h1>
            <p className="mt-4 max-w-xl font-label text-sm leading-relaxed text-background-200 md:text-base">
              {slides[index].desc}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/detail/ep-16"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-play-fill text-base leading-none"></i>
                </span>
                立即观看
              </Link>
              <Link
                to="/category/synopsis"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-background-50/10"
              >
                查看剧情介绍
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 flex items-center gap-3 md:right-12">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => handleStep(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15 md:h-9 md:w-9"
          >
            <i className="ri-arrow-left-s-line text-lg leading-none"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => handleStep(1)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15 md:h-9 md:w-9"
          >
            <i className="ri-arrow-right-s-line text-lg leading-none"></i>
          </button>
        </div>

        <div className="absolute bottom-7 left-4 flex items-center gap-2 md:left-8 lg:left-12">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张剧照`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-7 bg-accent-400" : "w-3 bg-background-50/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}